import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

// ============================================
// INTERFACES - RESPONSÁVEL
// ============================================

export interface IResponsavelResumo {
  idUsuario: number;
  nome: string;
  iniciais: string;
  email: string;
}

// ============================================
// INTERFACES - TAG
// ============================================

export interface ITag {
  idTag: number;
  nome: string;
  cor: string;
}

// ============================================
// INTERFACES - SUBTAREFA
// ============================================

export interface ISubtarefaResumo {
  idSubtarefa: number;
  nome: string;

  idTarefaStatus: number;
  nomeStatus: string;
  ordem: number;
  horasReal: number;
  /** Item 6.5: tempo real em segundos inteiros — fonte de verdade para hh:mm:ss. */
  totalSegundosReal?: number;
  estimativaHoras: number | null;
  responsaveis: IResponsavelResumo[];
}

export interface ISubtarefaDetalhada extends ISubtarefaResumo {
  descricao: string | null;
  previsaoInicio: string | null;
  previsaoTermino: string | null;
  inicioReal: string | null;
  terminoReal: string | null;
  dataCriacao: string;
  dataAtualizacao: string | null;
}

// ============================================
// INTERFACES - VÍNCULO
// ============================================

export interface IVinculo {
  idTarefaVinculo: number;
  idTarefaPrincipal: number;
  idTarefaVinculada: number;
  tipoVinculo: string;
  tituloVinculada: string;
  numeroVinculada: number;
  statusVinculada: string;
}

// ============================================
// INTERFACES - COMENTÁRIO
// ============================================

export interface IComentario {
  idTarefaComentario: number;
  idTarefa: number | null;
  idSubtarefa: number | null;
  texto: string;
  textoHtml: string | null;
  idUsuario: number;
  nomeUsuario: string;
  iniciaisUsuario: string;
  dataCriacao: string;
  dataAtualizacao: string | null;
  podeEditar: boolean;
  podeExcluir: boolean;
}

// ============================================
// INTERFACES - DOCUMENTO
// ============================================

export interface IDocumento {
  idTarefaDocumento: number;
  idTarefa: number | null;
  idSubtarefa: number | null;
  idTarefaAtividade: number | null;
  nomeArquivo: string;
  nomeOriginal: string;
  tamanhoBytes: number;
  tipoMime: string;
  idUsuarioUpload: number;
  nomeUsuarioUpload: string;
  dataUpload: string;
}

// ============================================
// INTERFACES - APONTAMENTO HORAS
// ============================================

export interface IApontamentoHora {
  idTarefaApontamentoHora: number;
  idTarefa: number | null;
  idSubtarefa: number | null;
  nomeSubtarefa: string | null;
  idUsuario: number;
  nomeUsuario: string;
  iniciaisUsuario: string;
  data: string;
  horaInicio: string;
  horaFim: string;
  minutos: number;
  /** Item 6.5: duração do apontamento em segundos — fonte para hh:mm:ss. */
  segundos?: number;
  observacao: string | null;
  dataCriacao: string;
  dataAtualizacao: string | null;
  podeEditar: boolean;
  podeExcluir: boolean;
}

export interface IResumoApontamentos {
  totalMinutos: number;
  /** Item 6.5: total em segundos — fonte para hh:mm:ss. */
  totalSegundos?: number;
  /** Item 6.5: total no formato "hh:mm:ss" (backend já formata). */
  totalHoras: string;
  apontamentos: IApontamentoHora[];
}

// ============================================
// INTERFACES - ATIVIDADE (TIPOLOGIAS)
// ============================================

export interface ITarefaStatusVM {
  idTarefaStatus: number;
  nome: string;
  ordem: number;
  cor: string;
}

export interface IAtividade {
  idTarefaAtividade: number;
  idTarefa: number;
  idTipologia: number | null;
  nomeTipologia: string | null;
  titulo: string;
  descricao: string | null;
  idTarefaStatus: number;
  idColunaKanban: number;
  nomeStatus: string;
  idResponsavel: number | null;
  nomeResponsavel: string | null;
  previsaoInicio: string | null;
  previsaoTermino: string | null;
  inicioReal: string | null;
  terminoReal: string | null;
  horasEstimadas: number | null;
  horasReais: number;
  ordem: number;
  quantidadeComentarios: number;
  dataCriacao: string;
  dataAtualizacao: string | null;
  idUsuarioCriacao: number;
  nomeUsuarioCriacao: string | null;
  // F04R-3 (reteste 21/08): responsáveis da subtarefa da atividade — o Responsável
  // do Caso é escolhido apenas entre eles (endpoint atividades/{id}/basica).
  idSubtarefa?: number | null;
  responsaveisSubtarefa?: {
    idUsuario: number;
    nome: string;
    iniciais?: string;
    email?: string | null;
  }[];
}

export interface IKanbanAtividades {
  colunas: ITarefaStatusVM[];
  atividades: IAtividade[];
}

export interface ICriarAtividadeRequest {
  idTarefa: number;
  idTipologia?: number;
  titulo: string;
  descricao?: string | null;
  idResponsavel?: number;
  previsaoInicio?: string;
  previsaoTermino?: string;
  horasEstimadas?: number;
  idTarefaStatus?: number;
  idColunaKanban?: number;
  idUsuarioCriacao?: number;
  replicarParaOutrasSubtarefas?: boolean; // Flag para replicação
}

export interface IAtualizarAtividadeRequest {
  idTarefaAtividade: number;
  idTipologia?: number;
  titulo: string;
  descricao?: string | null;
  idTarefaStatus?: number;
  idColunaKanban?: number;
  idResponsavel?: number;
  previsaoInicio?: string;
  previsaoTermino?: string;
  horasEstimadas?: number;
  inicioReal?: string;
  terminoReal?: string;
}

export interface IMoverAtividadeRequest {
  idTarefaAtividade: number;
  idTarefaStatusNovo: number;
}

// ============================================
// INTERFACES - TAREFA COMPLETA
// ============================================

export interface ITarefaCompleta {
  idTarefa: number;
  numero: number;
  titulo: string;
  descricao: string | null;
  idReferencia: number;
  nomeReferencia: string;
  idTipoAtividade: number | null;
  nomeTipoAtividade: string | null;
  ehTipologia?: boolean; // Indica se o tipo de atividade é "Cadastro/edição de tipologias"
  // Flags de gating sequencial para subtarefas de tipologia (item 3 do relatório FASE 04):
  ehPlanificacao?: boolean; // FASE 04 (item 3): é a PRIMEIRA subtarefa da tarefa
  planificacaoLiberada?: boolean; // Planificação já tem atividade + caso + tipologia
  subtarefaEditavel?: boolean; // Regra centralizada: !ehTipologia || ehPlanificacao || planificacaoLiberada
  idTarefaStatus: number;
  nomeStatus: string;
  idSolicitante: number | null; // Se selecionado da lista
  nomeSolicitante: string | null; // Nome completo (usuário ou texto livre)
  tarefaInterna: boolean;

  previsaoInicio: string | null;
  previsaoTermino: string | null;
  estimativaHoras: number | null;
  inicioReal: string | null;
  terminoReal: string | null;
  horasReal: number;
  /** Item 6.5: tempo real em segundos inteiros — fonte de verdade para hh:mm:ss. */
  totalSegundosReal?: number;
  idEmpresa: number;
  dataCriacao: string;
  dataAtualizacao: string | null;
  ativo: boolean;
  responsaveis: IResponsavelResumo[];
  tags: ITag[];
  subtarefas: ISubtarefaDetalhada[];
  vinculos: IVinculo[];
}

// ============================================
// INTERFACES - REQUESTS
// ============================================

export interface IAtualizarTarefaRequest {
  idTarefa: number;
  titulo: string;
  descricao?: string;
  idReferencia: number;
  idTipoAtividade?: number;

  idTarefaStatus: number;
  idSolicitante?: number; // ID do usuário selecionado
  nomeSolicitante?: string; // Nome completo (usuário selecionado ou texto livre)
  tarefaInterna: boolean;
  previsaoInicio?: string;
  previsaoTermino?: string;
  estimativaHoras?: number;
  idsResponsaveis: number[];
  idsTags: number[];
}

export interface IDuplicarTarefaRequest {
  idTarefa: number;
  novoTitulo?: string;
  copiarSubtarefas: boolean;
  copiarResponsaveis: boolean;
  copiarTags: boolean;
}

export interface ICriarSubtarefaDetalheRequest {
  idTarefa: number;
  nome: string;
  descricao?: string;

  previsaoInicio?: string;
  previsaoTermino?: string;
  estimativaHoras?: number;
  ordem: number;
  idsResponsaveis: number[];
}

export interface IAtualizarSubtarefaRequest {
  idSubtarefa: number;
  nome: string;
  descricao?: string;

  idTarefaStatus: number;
  previsaoInicio?: string;
  previsaoTermino?: string;
  estimativaHoras?: number;
  idsResponsaveis: number[];
  idsTags: number[];
}

export interface IReordenarSubtarefasTarefaRequest {
  idTarefa: number;
  idsOrdenados: number[]; // Array de IDs na ordem desejada (backend espera este formato)
}

export interface ICriarComentarioRequest {
  idTarefa?: number;
  idSubtarefa?: number;
  comentario: string; // Backend espera "comentario", não "texto"
  // IDs dos usuários mencionados via @ no comentário (item 6.2 — notificação por e-mail).
  // Opcional: backend dispara e-mail aos mencionados quando preenchido.
  idsUsuariosMencionados?: number[];
}

export interface IAtualizarComentarioRequest {
  idTarefaComentario: number; // Backend espera "idTarefaComentario", não "idComentario"
  comentario: string; // Backend espera "comentario", não "texto"
}

export interface IFiltroApontamentosRequest {
  idTarefa?: number;
  idSubtarefa?: number;
  dataInicio?: string;
  dataFim?: string;
}

export interface ICriarApontamentoRequest {
  idTarefa?: number;
  idSubtarefa?: number;
  data: string;
  horaInicio: string;
  horaFim: string;
  observacao?: string;
}

export interface IAtualizarApontamentoRequest {
  idApontamento: number;
  data: string;
  horaInicio: string;
  horaFim: string;
  observacao?: string;
}

export interface ICriarVinculoRequest {
  idTarefaPrincipal: number;
  idTarefaVinculada: number;
  tipoVinculo: string;
}

export interface ITarefaResumoVinculo {
  idTarefa: number;
  numero: number;
  titulo: string;
  nomeReferencia: string;
  nomeStatus: string;
}

export interface ISalvarTagRequest {
  idEmpresa: number;
  nome: string;
  cor?: string;
}

// ============================================
// SERVIÇOS - TAREFA COMPLETA
// ============================================

/**
 * Obtém tarefa completa com todas as informações para edição (API Nova)
 */
export const obterTarefaCompleta = async (
  idTarefa: number
): Promise<ITarefaCompleta | null> => {
  try {
    console.log("[obterTarefaCompleta] Buscando tarefa (API Nova):", idTarefa);
    const { data } = await api.get<ITarefaCompleta>(
      `/QuadroTarefa/ObterTarefaDetalhada/${idTarefa}`
    );
    console.log("[obterTarefaCompleta] Tarefa recebida:", data);
    console.log("[obterTarefaCompleta] Sub-tarefas recebidas:", data?.subtarefas);
    console.log("[obterTarefaCompleta] Quantidade de sub-tarefas:", data?.subtarefas?.length || 0);
    return data;
  } catch (error: any) {
    console.error("[obterTarefaCompleta] Erro ao buscar tarefa:", error);
    getErrorsByApi(error, "Não foi possível carregar a tarefa");
    return null;
  }
};

export const obterSubtarefaCompleta = async (
  idSubtarefa: number
): Promise<ITarefaCompleta | null> => {
  try {
    console.log("[obterSubtarefaCompleta] Buscando sub-tarefa (API Nova):", idSubtarefa);
    const { data } = await api.get<ITarefaCompleta>(
      `/QuadroTarefa/ObterSubtarefaDetalhada/${idSubtarefa}`
    );
    console.log("[obterSubtarefaCompleta] Sub-tarefa recebida:", data);

    console.log("[obterSubtarefaCompleta] nomeTipoAtividade:", data?.nomeTipoAtividade);
    return data || null;
  } catch (error: any) {
    console.error("[obterSubtarefaCompleta] Erro ao buscar sub-tarefa:", error);
    getErrorsByApi(error, "Não foi possível carregar a sub-tarefa");
    throw error;
  }
};

/**
 * Atualiza tarefa
 */
export const atualizarTarefa = async (
  request: IAtualizarTarefaRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/QuadroTarefa/AtualizarTarefa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível atualizar a tarefa");
    throw error;
  }
};

/**
 * Exclui tarefa
 * IMPORTANTE: Usa API Nova para manter consistência com o quadro Kanban
 */
export const excluirTarefa = async (idTarefa: number): Promise<boolean> => {
  try {
    const { data } = await api.delete<{ mensagem: string }>(
      `/Tarefa/ExcluirTarefa?id=${idTarefa}`
    );
    return !!data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir a tarefa");
    throw error;
  }
};

/**
 * Duplica tarefa
 */
export const duplicarTarefa = async (
  request: IDuplicarTarefaRequest
): Promise<{ idTarefa: number } | null> => {
  try {
    const { data } = await api.post<{ idTarefa: number }>(
      "/TarefaDetalhe/DuplicarTarefa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível duplicar a tarefa");
    throw error;
  }
};

// ============================================
// SERVIÇOS - SUBTAREFAS
// ============================================

/**
 * Cria sub-tarefa (usando API Nova para consistência com banco app-mof-db)
 */
export const criarSubtarefa = async (
  request: ICriarSubtarefaDetalheRequest
): Promise<{ idSubtarefa: number } | null> => {
  try {
    const { data } = await api.post<{ idSubtarefa: number }>(
      "/QuadroTarefa/CriarSubtarefa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível criar a sub-tarefa");
    throw error;
  }
};

/**
 * Atualiza sub-tarefa (Previsão, Estimativa, Status, Responsáveis).
 * Usa API Nova para persistir dados; Início/Término/Horas Real são preenchidos pelo timer e status.
 */
export const atualizarSubtarefa = async (
  request: IAtualizarSubtarefaRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/QuadroTarefa/AtualizarSubtarefa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível atualizar a sub-tarefa");
    throw error;
  }
};

/**
 * Exclui sub-tarefa
 */
export const excluirSubtarefa = async (
  idSubtarefa: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/TarefaDetalhe/ExcluirSubtarefaDetalhe/${idSubtarefa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir a sub-tarefa");
    throw error;
  }
};

/**
 * Reordena sub-tarefas
 */
export const reordenarSubtarefasTarefa = async (
  request: IReordenarSubtarefasTarefaRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/TarefaDetalhe/ReordenarSubtarefasTarefa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível reordenar as sub-tarefas");
    throw error;
  }
};

// ============================================
// SERVIÇOS - TAGS
// ============================================

/**
 * Lista tags da empresa
 */
export const listarTags = async (idEmpresa: number): Promise<ITag[]> => {
  try {
    const { data } = await api.get<ITag[]>(
      `/TarefaDetalhe/ListarTags/${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    // Silenciosamente retorna array vazio se endpoint não existir ou falhar
    // Evita quebrar o fluxo de criação de tarefa
    console.warn("Não foi possível carregar as tags:", error?.message || error);
    return [];
  }
};

/**
 * Cria ou obtém tag existente
 */
export const criarOuObterTag = async (
  request: ISalvarTagRequest
): Promise<ITag | null> => {
  try {
    const { data } = await api.post<ITag>(
      "/TarefaDetalhe/CriarOuObterTag",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível criar a tag");
    throw error;
  }
};

// ============================================
// SERVIÇOS - COMENTÁRIOS
// ============================================

/**
 * Lista comentários da tarefa
 */
export const listarComentarios = async (
  idTarefa: number
): Promise<IComentario[]> => {
  try {
    const { data } = await api.get<IComentario[]>(
      `/TarefaDetalhe/ListarComentarios/${idTarefa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os comentários");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Lista comentários da sub-tarefa
 */
export const listarComentariosSubtarefa = async (
  idSubtarefa: number
): Promise<IComentario[]> => {
  try {
    const { data } = await api.get<IComentario[]>(
      `/TarefaDetalhe/ListarComentariosSubtarefa/${idSubtarefa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os comentários");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Cria comentário
 */
export const criarComentario = async (
  request: ICriarComentarioRequest
): Promise<{ idTarefaComentario: number } | null> => {
  try {
    const { data } = await api.post<{ idTarefaComentario: number }>(
      "/TarefaDetalhe/CriarComentario",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível criar o comentário");
    throw error;
  }
};

/**
 * Atualiza comentário
 */
export const atualizarComentario = async (
  request: IAtualizarComentarioRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/TarefaDetalhe/AtualizarComentario",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível atualizar o comentário");
    throw error;
  }
};

/**
 * Exclui comentário
 */
export const excluirComentario = async (
  idComentario: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/TarefaDetalhe/ExcluirComentario/${idComentario}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir o comentário");
    throw error;
  }
};

// ============================================
// SERVIÇOS - APONTAMENTO DE HORAS
// ============================================

/**
 * Lista apontamentos da tarefa (API Nova)
 */
export const listarApontamentos = async (
  filtro: IFiltroApontamentosRequest
): Promise<IResumoApontamentos> => {
  try {
    const { data } = await api.post<IResumoApontamentos>(
      "/QuadroTarefa/ListarApontamentos",
      filtro
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os apontamentos");
    return { totalMinutos: 0, totalSegundos: 0, totalHoras: "00:00:00", apontamentos: [] };
  }
};

/**
 * Cria apontamento de horas (API Nova)
 */
export const criarApontamento = async (
  request: ICriarApontamentoRequest
): Promise<{ idTarefaApontamentoHora: number } | null> => {
  try {
    const { data } = await api.post<{ idTarefaApontamentoHora: number }>(
      "/QuadroTarefa/CriarApontamento",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível criar o apontamento");
    throw error;
  }
};

/**
 * Atualiza apontamento de horas (API Nova)
 */
export const atualizarApontamento = async (
  request: IAtualizarApontamentoRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/QuadroTarefa/AtualizarApontamento",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível atualizar o apontamento");
    throw error;
  }
};

/**
 * Exclui apontamento de horas (API Nova)
 */
export const excluirApontamento = async (
  idApontamento: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/QuadroTarefa/ExcluirApontamento/${idApontamento}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir o apontamento");
    throw error;
  }
};

// ============================================
// SERVIÇOS - VÍNCULOS
// ============================================

/**
 * Lista vínculos da tarefa
 */
export const listarVinculos = async (idTarefa: number): Promise<IVinculo[]> => {
  try {
    const { data } = await api.get<IVinculo[]>(
      `/TarefaDetalhe/ListarVinculos/${idTarefa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os vínculos");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Cria vínculo entre tarefas
 */
export const criarVinculo = async (
  request: ICriarVinculoRequest
): Promise<{ idTarefaVinculo: number } | null> => {
  try {
    const { data } = await api.post<{ idTarefaVinculo: number }>(
      "/TarefaDetalhe/CriarVinculo",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível criar o vínculo");
    throw error;
  }
};

/**
 * Exclui vínculo entre tarefas
 */
export const excluirVinculo = async (idVinculo: number): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/TarefaDetalhe/ExcluirVinculo/${idVinculo}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir o vínculo");
    throw error;
  }
};

// ============================================
// SERVIÇOS - DOCUMENTOS
// ============================================

/**
 * Lista documentos da tarefa ou sub-tarefa
 */
export const listarDocumentos = async (
  filtro: { idTarefa?: number; idSubtarefa?: number; idTarefaAtividade?: number | null }
): Promise<IDocumento[]> => {
  try {
    // Construir query params
    const queryParams = new URLSearchParams();
    if (filtro.idTarefa) queryParams.append("idTarefa", filtro.idTarefa.toString());
    if (filtro.idSubtarefa) queryParams.append("idSubtarefa", filtro.idSubtarefa.toString());
    if (filtro.idTarefaAtividade && filtro.idTarefaAtividade > 0) {
      queryParams.append("idTarefaAtividade", filtro.idTarefaAtividade.toString());
    }
    
    const queryString = queryParams.toString();
    console.log("[listarDocumentos] Parâmetros:", filtro, "Query:", queryString);
    
    const { data } = await api.get<IDocumento[]>(
      `/QuadroTarefa/ListarDocumentos?${queryString}`
    );
    console.log("[listarDocumentos] Documentos retornados:", data);
    return data || [];
  } catch (error: any) {
    console.error("[listarDocumentos] Erro ao listar documentos:", error);
    getErrorsByApi(error, "Não foi possível carregar os documentos");
    return [];
  }
};

/**
 * Upload de documento
 */
export const uploadDocumento = async (
  idTarefa: number | undefined,
  idSubtarefa: number | undefined,
  arquivo: File,
  idTarefaAtividade?: number | null
): Promise<{ idTarefaDocumento: number } | null> => {
  try {
    console.log("[uploadDocumento] Parâmetros recebidos:", { idTarefa, idSubtarefa, idTarefaAtividade, arquivo: arquivo?.name });
    
    const formData = new FormData();
    if (idTarefa) formData.append("idTarefa", idTarefa.toString());
    if (idSubtarefa) formData.append("idSubtarefa", idSubtarefa.toString());
    if (idTarefaAtividade) formData.append("idTarefaAtividade", idTarefaAtividade.toString());
    formData.append("arquivo", arquivo);
    
    // Log do FormData para depuração
    console.log("[uploadDocumento] FormData entries:");
    for (const pair of formData.entries()) {
      console.log(`  ${pair[0]}:`, pair[1]);
    }

    // IMPORTANTE: Para FormData, devemos remover o Content-Type default do axios
    // para que o navegador defina automaticamente com o boundary correto
    const { data } = await api.post<{ idTarefaDocumento: number }>(
      "/QuadroTarefa/UploadDocumento",
      formData,
      {
        headers: {
          "Content-Type": undefined,  // Remove o header default para FormData
        },
      }
    );
    console.log("[uploadDocumento] Sucesso:", data);
    return data;
  } catch (error: any) {
    console.error("[uploadDocumento] ERRO:", error);
    console.error("[uploadDocumento] Response data:", error?.response?.data);
    console.error("[uploadDocumento] Status:", error?.response?.status);
    getErrorsByApi(error, "Não foi possível fazer upload do documento");
    throw error;
  }
};

/**
 * Download de documento
 */
export const downloadDocumento = async (
  idDocumento: number,
  nomeArquivo: string
): Promise<void> => {
  try {
    const response = await api.get(
      `/QuadroTarefa/DownloadDocumento/${idDocumento}`,
      {
        responseType: "blob",
      }
    );

    // Criar link para download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", nomeArquivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível baixar o documento");
    throw error;
  }
};

/**
 * Exclui documento
 */
export const excluirDocumento = async (
  idDocumento: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/QuadroTarefa/ExcluirDocumento/${idDocumento}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir o documento");
    throw error;
  }
};

// ============================================
// SERVIÇOS - ATIVIDADES (KANBAN INTERNO)
// ============================================

/**
 * Obtém Kanban de atividades da tarefa
 */
export const obterKanbanAtividades = async (
  idTarefa: number
): Promise<IKanbanAtividades> => {
  try {
    const { data } = await api.get<IKanbanAtividades>(
      `/TarefaDetalhe/ObterKanbanAtividades/${idTarefa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar as atividades");
    return { colunas: [], atividades: [] };
  }
};

/**
 * Cria atividade
 */
export const criarAtividade = async (
  request: ICriarAtividadeRequest
): Promise<{ idTarefaAtividade: number } | null> => {
  try {
    console.log("[criarAtividade] Enviando request:", {
      idTarefa: request.idTarefa,
      titulo: request.titulo,
      replicarParaOutrasSubtarefas: request.replicarParaOutrasSubtarefas,
      requestCompleto: request,
    });
    
    const { data } = await api.post<{ idTarefaAtividade: number }>(
      "/TarefaDetalhe/CriarAtividade",
      request
    );
    
    console.log("[criarAtividade] Atividade criada com sucesso:", data);
    return data;
  } catch (error: any) {
    console.error("[criarAtividade] Erro ao criar atividade:", error);
    getErrorsByApi(error, "Não foi possível criar a atividade");
    throw error;
  }
};

/**
 * Atualiza atividade
 */
export const atualizarAtividade = async (
  request: IAtualizarAtividadeRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/TarefaDetalhe/AtualizarAtividade",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível atualizar a atividade");
    throw error;
  }
};

/**
 * Move atividade no Kanban
 */
export const moverAtividade = async (
  request: IMoverAtividadeRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/TarefaDetalhe/MoverAtividade",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível mover a atividade");
    throw error;
  }
};

/**
 * Exclui atividade
 */
export const excluirAtividade = async (
  idAtividade: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/TarefaDetalhe/ExcluirAtividade/${idAtividade}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir a atividade");
    throw error;
  }
};

// ============================================
// SERVIÇOS - BUSCA DE TAREFAS (PARA VÍNCULO)
// ============================================

/**
 * Busca tarefas para vinculação
 */
export const buscarTarefasParaVinculo = async (
  termo: string,
  idTarefaExcluir?: number
): Promise<ITarefaResumoVinculo[]> => {
  try {
    const { data } = await api.get<ITarefaResumoVinculo[]>(
      `/TarefaDetalhe/BuscarTarefasParaVinculo`,
      {
        params: {
          termo,
          idTarefaExcluir,
        },
      }
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível buscar tarefas");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

// ============================================
// INTERFACES - CASO
// ============================================

export interface ICasoTipologia {
  idTarefaCasoTipologia: number;
  idTarefaCaso: number;
  idTipologia: number | null;
  codigo: string;
  descricao: string;
  idLinha: number | null;
  nomeLinha?: string;
  idReferencia: number | null;
  nomeReferencia?: string;
  // FASE 04 (item 5a): o grid do Caso apresenta o Projetista, nao a Referencia.
  idProjetista?: number | null;
  nomeProjetista?: string;
  novo: boolean;
  concluido: boolean;
  ordem: number;
  dataCriacao: string;
  ativo: boolean;
}

export interface ICasoResponsavel {
  idTarefaCasoResponsavel: number;
  idTarefaCaso: number;
  idUsuario: number;
  nomeUsuario: string;
  emailUsuario: string;
  avatarUsuario?: string;
  dataCriacao: string;
}

export interface ICaso {
  idTarefaCaso: number;
  idTarefaAtividade: number;
  descricao: string;
  detalhes: string | null;
  idResponsavel: number | null;
  nomeResponsavel: string | null;
  idTarefaStatus: number;
  nomeStatus: string;
  concluido: boolean;
  dataCriacao: string;
  dataAtualizacao: string | null;
  dataConclusao: string | null;
  ativo: boolean;
  tipologias: ICasoTipologia[];
  responsaveis: ICasoResponsavel[];
}

export interface ICriarCasoRequest {
  idTarefaAtividade?: number; // Opcional no frontend, será definido pelo backend
  descricao: string;
  detalhes?: string | null;
  idResponsavel?: number | null;
  idsResponsaveis?: number[];
  idTarefaStatus?: number;
}

export interface IAtualizarCasoRequest {
  idTarefaCaso: number;
  descricao: string;
  detalhes?: string | null;
  idResponsavel?: number | null;
  idsResponsaveis?: number[];
  idTarefaStatus: number;
}

export interface ICopiarCasoRequest {
  idTarefaCasoOrigem: number;
  idTarefaCasoDestino: number;
}

export interface IImportarTipologiasMOFRequest {
  idTarefaCaso: number;
  idsTipologias: number[];
}

export interface INovaTipologiaRequest {
  codigo: string;
  descricao: string;
  idLinha: number;
  // N9.1: a coluna Referência foi removida do modal; o backend aceita IdReferencia nulo
  idReferencia?: number | null;
  // FASE 04 (item 5a): o modal ja pergunta o projetista — passa a enviá-lo.
  idProjetista?: number | null;
}

export interface ICriarTipologiasNovasRequest {
  idTarefaCaso: number;
  tipologias: INovaTipologiaRequest[];
}

// ============================================
// SERVIÇOS - CASO
// ============================================

/**
 * Obtém dados básicos de uma Atividade (ID e Título) para exibição no modal de Caso
 */
export const obterAtividadeBasica = async (
  idTarefaAtividade: number
): Promise<IAtividade | null> => {
  try {
    const { data } = await api.get<IAtividade>(
      `/TarefaDetalhe/atividades/${idTarefaAtividade}/basica`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar dados da atividade");
    return null;
  }
};

/**
 * Lista todos os Casos de uma Atividade
 */
export const listarCasosPorAtividade = async (
  idTarefaAtividade: number
): Promise<ICaso[]> => {
  try {
    const { data } = await api.get<ICaso[]>(
      `/TarefaDetalhe/atividades/${idTarefaAtividade}/casos`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível listar casos");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Obtém um Caso completo com todos os relacionamentos
 */
export const obterCasoCompleto = async (
  idTarefaCaso: number
): Promise<ICaso | null> => {
  try {
    const { data } = await api.get<ICaso>(
      `/TarefaDetalhe/casos/${idTarefaCaso}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível obter caso");
    return null;
  }
};

/**
 * Cria um novo Caso vinculado a uma Atividade
 */
export const criarCaso = async (
  idTarefaAtividade: number,
  request: ICriarCasoRequest
): Promise<ICaso> => {
  try {
    console.log("[criarCaso] Enviando request:", {
      idTarefaAtividade,
      request,
      url: `/TarefaDetalhe/atividades/${idTarefaAtividade}/casos`,
    });
    
    const { data } = await api.post<{ idTarefaCaso: number }>(
      `/TarefaDetalhe/atividades/${idTarefaAtividade}/casos`,
      request
    );
    
    console.log("[criarCaso] Caso criado com sucesso:", data);

    // Enriquecimento best-effort: o POST já persistiu o caso. Se o refetch falhar
    // ou retornar null, devolvemos um ICaso mínimo (com o id retornado e os campos
    // do request) em vez de lançar — assim o chamador habilita os botões de
    // tipologia mesmo quando o refetch está instável (itens 6/7 do relatório).
    const casoCompleto = await obterCasoCompleto(data.idTarefaCaso);
    if (casoCompleto) {
      return casoCompleto;
    }

    console.warn(
      "[criarCaso] Refetch não retornou dados; usando caso mínimo a partir do request"
    );
    return {
      idTarefaCaso: data.idTarefaCaso,
      idTarefaAtividade: request.idTarefaAtividade ?? idTarefaAtividade,
      descricao: request.descricao,
      detalhes: request.detalhes ?? null,
      idResponsavel: request.idResponsavel ?? null,
      nomeResponsavel: null,
      idTarefaStatus: request.idTarefaStatus ?? 1,
      nomeStatus: "",
      concluido: false,
      dataCriacao: new Date().toISOString(),
      dataAtualizacao: null,
      dataConclusao: null,
      ativo: true,
      tipologias: [],
      responsaveis: [],
    };
  } catch (error: any) {
    console.error("[criarCaso] Erro ao criar caso:", error);
    getErrorsByApi(error, "Não foi possível criar caso");
    throw error;
  }
};

/**
 * Atualiza um Caso existente
 */
export const atualizarCaso = async (
  idTarefaCaso: number,
  request: IAtualizarCasoRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}`,
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível atualizar caso");
    throw error;
  }
};

/**
 * Exclui um Caso (soft delete)
 */
export const excluirCaso = async (
  idTarefaCaso: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível excluir caso");
    throw error;
  }
};

/**
 * Conclui um Caso
 */
export const concluirCaso = async (
  idTarefaCaso: number
): Promise<boolean> => {
  try {
    console.log("[concluirCaso] Iniciando conclusão do caso:", idTarefaCaso);
    const { data } = await api.post<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/concluir`
    );
    console.log("[concluirCaso] Caso concluído com sucesso:", data);
    return data;
  } catch (error: any) {
    console.error("[concluirCaso] Erro ao concluir caso:", error);
    console.error("[concluirCaso] Tipo do erro:", typeof error);
    console.error("[concluirCaso] error.response:", error.response);
    console.error("[concluirCaso] error.response?.data:", error.response?.data);
    console.error("[concluirCaso] error.data:", error.data);
    console.error("[concluirCaso] error.message:", error.message);
    
    // Extrair mensagem do erro de forma mais robusta
    let errorMessage = "Não foi possível concluir caso";
    
    // Tentar extrair de diferentes formatos de resposta
    if (error.response?.data) {
      const responseData = error.response.data;
      if (typeof responseData === "string") {
        errorMessage = responseData;
      } else if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.Mensagens && Array.isArray(responseData.Mensagens)) {
        errorMessage = responseData.Mensagens.join(", ");
      } else if (responseData.Mensagens && typeof responseData.Mensagens === "string") {
        errorMessage = responseData.Mensagens;
      }
    } else if (error.data) {
      const errorData = error.data;
      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.Mensagens && Array.isArray(errorData.Mensagens)) {
        errorMessage = errorData.Mensagens.join(", ");
      } else if (errorData.Mensagens && typeof errorData.Mensagens === "string") {
        errorMessage = errorData.Mensagens;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.log("[concluirCaso] Mensagem de erro extraída:", errorMessage);
    
    // Criar erro com mensagem específica
    const customError = new Error(errorMessage);
    (customError as any).response = error.response || error;
    (customError as any).data = error.data || error.response?.data;
    throw customError;
  }
};

/**
 * Inicia trabalho no Caso (cria apontamento de horas)
 */
export const iniciarTrabalhoNoCaso = async (
  idTarefaCaso: number
): Promise<number> => {
  try {
    const { data } = await api.post<{ idApontamento: number }>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/iniciar-trabalho`
    );
    return data.idApontamento;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível iniciar trabalho no caso");
    throw error;
  }
};

/**
 * Pausa trabalho no Caso (finaliza apontamento de horas)
 */
export const pausarTrabalhoNoCaso = async (
  idTarefaCaso: number
): Promise<boolean> => {
  try {
    const { data } = await api.post<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/pausar-trabalho`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível pausar trabalho no caso");
    throw error;
  }
};

/**
 * Importa tipologias do MOF para o Caso
 */
export const importarTipologiasDoMOF = async (
  idTarefaCaso: number,
  request: IImportarTipologiasMOFRequest
): Promise<boolean> => {
  try {
    const { data } = await api.post<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/tipologias/importar-mof`,
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível importar tipologias do MOF");
    throw error;
  }
};

/**
 * Cria tipologias novas e adiciona ao Caso
 */
export const criarTipologiasNovas = async (
  idTarefaCaso: number,
  request: ICriarTipologiasNovasRequest
): Promise<boolean> => {
  try {
    const { data } = await api.post<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/tipologias/criar-novas`,
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível criar tipologias novas");
    throw error;
  }
};

/**
 * Copia tipologias de outro Caso
 */
export const copiarTipologiasDeOutroCaso = async (
  idTarefaCaso: number,
  request: ICopiarCasoRequest
): Promise<boolean> => {
  try {
    const { data } = await api.post<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/tipologias/copiar-de-outro-caso`,
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível copiar tipologias de outro caso");
    throw error;
  }
};

/**
 * Remove uma tipologia do Caso
 */
export const removerTipologiaDoCaso = async (
  idTarefaCaso: number,
  idTarefaCasoTipologia: number
): Promise<boolean> => {
  try {
    const { data } = await api.delete<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/tipologias/${idTarefaCasoTipologia}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível remover tipologia do caso");
    throw error;
  }
};

/**
 * Marca uma tipologia como concluída ou não
 */
export const marcarTipologiaComoConcluida = async (
  idTarefaCaso: number,
  idTarefaCasoTipologia: number,
  concluido: boolean
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      `/TarefaDetalhe/casos/${idTarefaCaso}/tipologias/${idTarefaCasoTipologia}/concluir`,
      concluido
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível marcar tipologia como concluída");
    throw error;
  }
};

